// Secure Logging Service - Production-safe logging with sensitive data protection
class SecureLoggingService {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.isDebugMode = process.env.REACT_APP_DEBUG_MODE === 'true';
    this.logLevel = process.env.REACT_APP_LOG_LEVEL || 'info';
  }

  // Check if logging is allowed
  canLog(level = 'info') {
    if (!this.isDevelopment && !this.isDebugMode) {
      return false;
    }

    const levels = ['error', 'warn', 'info', 'debug'];
    const currentLevelIndex = levels.indexOf(this.logLevel);
    const requestedLevelIndex = levels.indexOf(level);
    
    return requestedLevelIndex <= currentLevelIndex;
  }

  // Sanitize sensitive data
  sanitizeData(data) {
    if (!data || typeof data !== 'object') {
      return data;
    }

    const sensitiveKeys = [
      'password', 'token', 'otp', 'secret', 'key', 'auth', 'credential',
      'ssn', 'aadhar', 'pan', 'bank', 'card', 'cvv', 'pin',
      'email', 'phone', 'mobile', 'address', 'pincode'
    ];

    const sanitized = { ...data };
    
    Object.keys(sanitized).forEach(key => {
      const lowerKey = key.toLowerCase();
      if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive))) {
        sanitized[key] = '***REDACTED***';
      } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
        sanitized[key] = this.sanitizeData(sanitized[key]);
      }
    });

    return sanitized;
  }

  // Secure log method
  log(level, message, data = null) {
    if (!this.canLog(level)) {
      return;
    }

    const sanitizedData = data ? this.sanitizeData(data) : null;
    const timestamp = new Date().toISOString();
    
    const logMessage = `[${timestamp}] [${level.toUpperCase()}] ${message}`;
    
    // Use console directly to avoid circular calls
    switch (level) {
      case 'error':
        console.error(logMessage, sanitizedData);
        break;
      case 'warn':
        console.warn(logMessage, sanitizedData);
        break;
      case 'info':
        console.info(logMessage, sanitizedData);
        break;
      case 'debug':
        console.debug(logMessage, sanitizedData);
        break;
      default:
        console.log(logMessage, sanitizedData);
    }
  }

  // Convenience methods
  error(message, data = null) {
    this.log('error', message, data);
  }

  warn(message, data = null) {
    this.log('warn', message, data);
  }

  info(message, data = null) {
    this.log('info', message, data);
  }

  debug(message, data = null) {
    this.log('debug', message, data);
  }

  // Security-specific logging
  security(message, data = null) {
    if (this.canLog('warn')) {
      console.warn(`[SECURITY] ${message}`, data ? this.sanitizeData(data) : null);
    }
  }

  // Performance logging
  performance(message, data = null) {
    if (this.canLog('info')) {
      console.info(`[PERFORMANCE] ${message}`, data ? this.sanitizeData(data) : null);
    }
  }

  // User action logging (for analytics)
  userAction(action, data = null) {
    if (this.canLog('info')) {
      console.info(`[USER_ACTION] ${action}`, data ? this.sanitizeData(data) : null);
    }
  }

  // API call logging
  apiCall(method, url, data = null) {
    if (this.canLog('debug')) {
      console.debug(`[API] ${method} ${url}`, data ? this.sanitizeData(data) : null);
    }
  }

  // Form validation logging
  formValidation(formName, errors = null) {
    if (this.canLog('debug')) {
      console.debug(`[FORM] ${formName} validation`, errors ? this.sanitizeData(errors) : null);
    }
  }

  // Firebase operation logging
  firebase(operation, collection, data = null) {
    if (this.canLog('debug')) {
      console.debug(`[FIREBASE] ${operation} ${collection}`, data ? this.sanitizeData(data) : null);
    }
  }

  // Payment logging (extra sensitive)
  payment(operation, data = null) {
    if (this.canLog('info')) {
      const sanitized = data ? {
        ...this.sanitizeData(data),
        amount: data.amount ? '***REDACTED***' : undefined,
        transactionId: data.transactionId ? '***REDACTED***' : undefined
      } : null;
      console.info(`[PAYMENT] ${operation}`, sanitized);
    }
  }

  // Student data logging (extra sensitive)
  student(operation, data = null) {
    if (this.canLog('info')) {
      const sanitized = data ? {
        ...this.sanitizeData(data),
        name: data.name ? '***REDACTED***' : undefined,
        email: data.email ? '***REDACTED***' : undefined,
        mobile: data.mobile ? '***REDACTED***' : undefined,
        aadhar: data.aadhar ? '***REDACTED***' : undefined
      } : null;
      console.info(`[STUDENT] ${operation}`, sanitized);
    }
  }

  // Admin operation logging
  admin(operation, data = null) {
    if (this.canLog('warn')) {
      console.warn(`[ADMIN] ${operation}`, data ? this.sanitizeData(data) : null);
    }
  }

  // System health logging
  system(message, data = null) {
    if (this.canLog('info')) {
      console.info(`[SYSTEM] ${message}`, data ? this.sanitizeData(data) : null);
    }
  }

  // Error boundary logging
  errorBoundary(error, errorInfo = null) {
    if (this.canLog('error')) {
      console.error('[ERROR_BOUNDARY]', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo?.componentStack
      });
    }
  }

  // Network request logging
  network(method, url, status, data = null) {
    if (this.canLog('info')) {
      console.info(`[NETWORK] ${method} ${url} - ${status}`, data ? this.sanitizeData(data) : null);
    }
  }

  // Local storage operation logging
  storage(operation, key, data = null) {
    if (this.canLog('debug')) {
      console.debug(`[STORAGE] ${operation} ${key}`, data ? this.sanitizeData(data) : null);
    }
  }

  // Session management logging
  session(operation, data = null) {
    if (this.canLog('warn')) {
      console.warn(`[SESSION] ${operation}`, data ? this.sanitizeData(data) : null);
    }
  }

  // OTP operation logging (extra sensitive)
  otp(operation, data = null) {
    if (this.canLog('warn')) {
      const sanitized = data ? {
        ...this.sanitizeData(data),
        otp: data.otp ? '***REDACTED***' : undefined,
        token: data.token ? '***REDACTED***' : undefined
      } : null;
      console.warn(`[OTP] ${operation}`, sanitized);
    }
  }

  // XSS attempt logging
  xss(attempt, data = null) {
    if (this.canLog('error')) {
      console.error(`[XSS] ${attempt}`, data ? this.sanitizeData(data) : null);
    }
  }

  // Race condition logging
  raceCondition(operation, data = null) {
    if (this.canLog('warn')) {
      console.warn(`[RACE_CONDITION] ${operation}`, data ? this.sanitizeData(data) : null);
    }
  }
}

const secureLogger = new SecureLoggingService();
export default secureLogger;
